// Phase 1a Polish — Assets landing grid.
// Replaces the single-table listing with a category landing:
//   Dynamic types (SmartCharger, TimeMachine, RTK, GroundStation) each get a clickable card
//     showing total + per-status breakdown + the ownership breakdown (internal / lease /
//     sold, plus unclassified when a type still has rows nobody has audited).
//     Click → /assets/type/:typeKey per-type list.
//   Stable types get a single consolidated link card ("Stable Assets — N items")
//     since they share a simpler read-only view (no per-type drill-down).
//   Kit types get cards too (just ground_station_kit for now).

import React, { useEffect, useState } from "react";
import { buttonClass } from '../components/ui/Button';
import { Link, useHistory } from "react-router-dom";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { AssetTypeCount } from "./types";
import PageLayout from "../components/ui/PageLayout";
import Tag from "../components/ui/Tag";
import LogoSpinner from "../components/ui/LogoSpinner";
import FadeIn from "../components/ui/FadeIn";
import { ListRow, ListStack } from "../components/ui/ListRow";
import Tooltip from "../components/ui/Tooltip";

const AssetsPage: React.FC = () => {
	const api = useApi();
	const toast = useToast();
	const history = useHistory();

	const [counts, setCounts] = useState<AssetTypeCount[]>([]);
	const [loading, setLoading] = useState(true);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			try {
				const c = await api.get("/assets/by-type-counts");
				if (!cancelled) setCounts(c);
			} catch {
				if (!cancelled) toast.error("Failed to load asset overview");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
	}, []);

	const dynamic = counts.filter((c) => c.category === "dynamic");
	const kits = counts.filter((c) => c.category === "kit");
	const stable = counts.filter((c) => c.category === "stable");
	// "Active" = total minus retired. Retired items aren't operationally meaningful at
	// the landing-page level, so the headline number reflects what's actually in service.
	const stableActive = stable.reduce((sum, c) => sum + (c.total - c.retired), 0);

	return (
		<PageLayout kind="workspace" className="pt-4 space-y-5">
			<div className="flex justify-between items-center">
				<h1 className="text-2xl font-bold text-primary">Assets</h1>
				<div className="flex gap-2">
					<Tooltip label="Manage asset types">
						<Link
							to="/admin/asset-types"
							className={buttonClass('tonal', 'md')}
						>
							Asset Types
						</Link>
					</Tooltip>
					<Link
						to="/assets/new"
						className={buttonClass('filled', 'md')}
					>
						+ New Asset
					</Link>
				</div>
			</div>

			{loading && <LogoSpinner label="Loading assets" />}

			{!loading && (
				<FadeIn className="space-y-5">
					<SectionHeader
						label="Dynamic"
						description="Individually-tracked gear with per-type detail"
					/>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
						{dynamic.map((c) => (
							<TypeCard
								key={c.id}
								count={c}
								onClick={() =>
									history.push(`/assets/type/${c.key}`)
								}
							/>
						))}
					</div>

					{kits.length > 0 && (
						<>
							<SectionHeader
								label="Kits"
								description="Composed assets containing child gear"
							/>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
								{kits.map((c) => (
									<TypeCard
										key={c.id}
										count={c}
										onClick={() =>
											history.push(
												`/assets/type/${c.key}`,
											)
										}
									/>
								))}
							</div>
						</>
					)}

					<SectionHeader
						label="Stable"
						description="Interchangeable consumables: tarps, cables, Pelican, Anker, Starlink, etc."
					/>
					<ListStack
						items={[stable]}
						getKey={() => "stable"}
						label="Stable assets"
						renderRow={() => (
							<ListRow
								to="/assets/stable"
								title="Stable"
								meta={`${stable.length} types, ${stableActive} active items`}
							/>
						)}
					/>
				</FadeIn>
			)}
		</PageLayout>
	);
};

const SectionHeader: React.FC<{ label: string; description: string }> = ({
	label,
	description,
}) => (
	<div className="pt-3">
		<div className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold">
			{label}
		</div>
		<div className="text-xs text-on-surface-variant mt-0.5">{description}</div>
	</div>
);

const TypeCard: React.FC<{ count: AssetTypeCount; onClick: () => void }> = ({
	count,
	onClick,
}) => {
	// Headline number reflects ACTIVE assets (total minus retired) so the landing
	// page doesn't make an empty inventory look full just because old items linger.
	// "Empty" still uses raw total so a card with only retired items renders dimmed
	// rather than misleadingly looking like there's something in service.
	// The ownership counts below are over the type's ACTIVE rows (retired excluded, the
	// same default the type list applies), so they sum to the headline. The total is
	// spelled out beside it whenever retired rows make the two differ.
	const active = count.total - count.retired;
	const isEmpty = count.total === 0;
	return (
		<button
			onClick={onClick}
			className={`text-left bg-surface-container-low border rounded-md p-4 transition-all duration-150 ${
				isEmpty
					? "border-transparent opacity-60 hover:opacity-100 hover:border-primary/20"
					: "border-transparent hover:border-primary/40"
			}`}
			style={{
				boxShadow: isEmpty
					? undefined
					: "0 0 30px rgba(255,204,51,0.02)",
			}}
		>
			<div className="flex justify-between items-baseline mb-2">
				<div className="text-white font-semibold text-sm">
					{count.label}
				</div>
				<div className="flex items-baseline gap-1.5">
					<div className="text-primary text-2xl font-bold tabular-nums" title={`${active} active · ${count.retired} retired`}>
						{active}
					</div>
					{count.retired > 0 && (
						<span className="text-on-surface-variant text-xs tabular-nums">
							of {count.total}
						</span>
					)}
				</div>
			</div>
			{!isEmpty && (
				<div className="flex gap-1.5 flex-wrap text-[10px]">
					{count.ready > 0 && (
						<Pill color="green" label={`${count.ready} ready`} />
					)}
					{count.building > 0 && (
						<Pill
							color="amber"
							label={`${count.building} building`}
						/>
					)}
					{count.flagged > 0 && (
						<Pill
							color="yellow"
							label={`${count.flagged} flagged`}
						/>
					)}
					{count.ooc > 0 && (
						<Pill color="red" label={`${count.ooc} OOC`} />
					)}
					{count.checked_out > 0 && (
						<Pill color="blue" label={`${count.checked_out} out`} />
					)}
					{count.needs_update > 0 && (
						<Pill
							color="sky"
							label={`${count.needs_update} need update`}
						/>
					)}
				</div>
			)}
			{/* Ownership: whose is it? Quieter than the status pills on purpose — the
				status row is about what the gear can do today, this row is about the
				commercial state, and one card should not read as ten alarms. Internal,
				Lease and Sold always show (a zero is an answer); unclassified appears
				only when there is something to audit, and it is the one warm value
				because it is a worklist rather than a state. */}
			{!isEmpty && (
				<div className="flex gap-x-3 gap-y-1 flex-wrap text-[10px] text-on-surface-variant mt-2">
					<OwnershipStat label="Internal" value={count.internal} />
					<OwnershipStat label="Lease" value={count.lease} />
					<OwnershipStat label="Sold" value={count.sold} />
					{count.unclassified > 0 && (
						<span className="text-amber-300/80">
							<span className="tabular-nums font-semibold">
								{count.unclassified}
							</span>{" "}
							unclassified
						</span>
					)}
				</div>
			)}
			{isEmpty && (
				<div className="text-on-surface-variant text-xs italic">None yet</div>
			)}
		</button>
	);
};

const OwnershipStat: React.FC<{ label: string; value: number }> = ({
	label,
	value,
}) => (
	<span>
		{label}{" "}
		<span className="tabular-nums text-on-surface font-medium">{value}</span>
	</span>
);

const PILL_COLORS: Record<string, string> = {
	green: "#86efac",
	amber: "#fcd34d",
	yellow: "#fde047",
	red: "#fca5a5",
	blue: "#93c5fd",
	sky: "#7dd3fc",
};

const Pill: React.FC<{ color: keyof typeof PILL_COLORS; label: string }> = ({
	color,
	label,
}) => (
	<Tag hex={PILL_COLORS[color]} size="sm">
		{label}
	</Tag>
);

export default AssetsPage;
